import {ScreenLoadService} from './shared/performance/screen-load.service';
import {ScreenLoadComponent} from './shared/performance/screen-load.component';
import {UniversalScannerComponent} from './shared/scanning/universal-scanner.component';
import {HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi} from '@angular/common/http';
import {APP_INITIALIZER, NgModule, Optional, PLATFORM_ID, REQUEST} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import {BrowserModule, provideClientHydration, withEventReplay} from '@angular/platform-browser';
import {RouteReuseStrategy} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {provideAteEditor} from '@flogeez/angular-tiptap-editor';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {addIcons} from 'ionicons';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {AuthRedirectInterceptor} from './auth/auth-redirect.interceptor';
import {AuthTokenInterceptor} from './auth/auth-token.interceptor';
import {AuthLayoutComponent} from './layouts/auth-layout/auth-layout.component';
import {ShellLayoutComponent} from './layouts/shell-layout/shell-layout.component';
import {QuickNavigationComponent} from './shared/navigation/quick-navigation.component';
import {ShellBreadcrumbsComponent} from './layouts/shell-layout/shell-breadcrumbs.component';
import {AppLanguageService} from './shared/i18n/app-language.service';
import {LanguageInterceptor} from './shared/i18n/language.interceptor';
import {ServerErrorInterceptor} from './shared/errors/server-error.interceptor';
import {SmNavBarModule} from './shared/components/sm-nav-bar/sm-nav-bar.module';
import {SmSearchControlModule} from './shared/components/sm-search-control/sm-search-control.module';
import {SmGlobalSearchResultsModule} from './shared/components/sm-global-search-results/sm-global-search-results.module';
import {SmBackendRecoveryBannerModule} from './shared/components/sm-backend-recovery-banner/sm-backend-recovery-banner.module';
import {SmQrScannerOverlayModule} from './shared/components/sm-qr-scanner-overlay/sm-qr-scanner-overlay.module';
import {SmConfirmationDialogHostModule} from './shared/components/sm-confirmation-dialog/sm-confirmation-dialog-host.module';
import {SmConfirmationDialogModule} from './shared/components/sm-confirmation-dialog/sm-confirmation-dialog.module';
import {ServerErrorDialogHostModule} from './shared/errors/server-error-dialog-host.module';
import {SsrDemoComponent} from './ssr-demo/ssr-demo.component';
import {SmAppVersionModule} from './shared/components/sm-app-version/sm-app-version.module';
import {SmPolicyLinkModule} from './shared/components/sm-policy-link/sm-policy-link.module';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {reportsFeature} from './reports/store/reports.reducer';
import {ReportsEffects} from './reports/store/reports.effects';
import {SmAppUpdateProgressModule} from './shared/components/sm-app-update-progress/sm-app-update-progress.module';
import {ApiCompatibilityInterceptor} from './shared/api/api-compatibility.interceptor';
import {WebVersionHintComponent} from './shared/web-version/web-version-hint.component';

export function initializeAppLanguage(
  appLanguageService: AppLanguageService,
  platformId: object,
  request: Request | null,
): () => Promise<void> | void {
  return () => {
    const initialization = appLanguageService.initialize(request?.url);

    if (isPlatformServer(platformId)) {
      return initialization;
    }

    return initialization.catch((error) => {
      console.error(error);
    });
  };
}

@NgModule({
  declarations: [
    AppComponent,
    AuthLayoutComponent,
    ShellLayoutComponent,
    ShellBreadcrumbsComponent,
    SsrDemoComponent,
  ],
  imports: [

    ScreenLoadComponent,
    WebVersionHintComponent,
    BrowserModule,
    StoreModule.forRoot({}),
    StoreModule.forFeature(reportsFeature),
    EffectsModule.forRoot([ReportsEffects]),
    IonicModule.forRoot(),
    TranslateModule.forRoot({
      fallbackLang: 'en',
    }),
    SmNavBarModule,
    SmSearchControlModule,
    SmGlobalSearchResultsModule,
    SmBackendRecoveryBannerModule,
    SmAppUpdateProgressModule,
    SmQrScannerOverlayModule,
    UniversalScannerComponent,
    QuickNavigationComponent,
    SmConfirmationDialogModule,
    SmConfirmationDialogHostModule,
    ServerErrorDialogHostModule,
    AppRoutingModule,
    SmAppVersionModule,
    SmPolicyLinkModule,
  ],
  providers: [
    ScreenLoadService,
    {provide: APP_INITIALIZER, useFactory: (metrics: ScreenLoadService) => () => { void metrics; }, deps: [ScreenLoadService], multi: true},
    provideAteEditor(),
    ...provideTranslateHttpLoader({
      prefix: '/assets/i18n/',
      suffix: '.json',
    }),
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppLanguage,
      deps: [AppLanguageService, PLATFORM_ID, [new Optional(), REQUEST]],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthRedirectInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiCompatibilityInterceptor,
      multi: true,
    },
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideClientHydration(withEventReplay()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    addIcons({
      'inventory-move': '/assets/icons/inventory-move.svg',
      'inventory-move-into': '/assets/icons/inventory-move-into.svg',
    });
  }
}
